import React from "react";
import { isDraftJsState, jsonTryParse } from "core/utils/json";
import Interweave from "interweave";
import { convertFromRaw } from "draft-js";
import { Options, stateToHTML } from "draft-js-export-html";
import { behanceColor } from "assets/jss/default/theme";
import isString from "lodash/isString";
import { styled } from "@mui/material/styles";

// noinspection JSUnusedGlobalSymbols
const exportOptions: Options = {
  defaultBlockTag: undefined, // interweave takes care of this for us, so this should be null
  entityStyleFn: entity => {
    const entityType = entity.getType().toLowerCase();
    if (entityType === "mention") {
      const data = entity.getData();
      return {
        element: "a",
        attributes: {
          href: "#",
          "data-username": data.mention.username,
          "data-name": data.mention.name,
          title: data.mention.email
        },
        style: {
          fontWeight: "bold",
          color: behanceColor
        }
      };
    }
    if (entityType === "link") {
      const data = entity.getData();
      return {
        element: "a",
        attributes: {
          href: data.url,
          target: "_blank"
        },
        style: {}
      };
    }
  }
};

/**
 * Converts the raw draft-js editor state which we stored in the db to html and safely injects it into the virtual dom.
 * @param rawState The raw state we stored in the db upon saving the contents of an text editor.
 * @return {JSX.Element} The converted html
 * @constructor
 */
export const EditorStateToHtml = ({
  rawState
}: {
  rawState: string | Object;
}): JSX.Element => {
  // in db for now we store the editor state as json string instead of a postgresql JSON field type.
  let jsonState: any;
  if (isString(rawState)) {
    jsonState = jsonTryParse(rawState);
  } else {
    jsonState = rawState;
  }

  // https://interweave.dev/docs/
  // if inline styles are required check the docs: https://www.npmjs.com/package/draft-js-export-html
  // for now we allow simple text as a backward compatibility with the previous version of the system
  // What's happening here? We take the raw editor state from the SCAPI and we convert it to HTML, then we
  // take the HTML and parse it then inject it into the virtual DOM via Interweave.
  return isDraftJsState(jsonState) ? (
    <Interweave
      content={stateToHTML(convertFromRaw(jsonState), exportOptions)}
      containerTagName="div" // this is why we set the "defaultBlockTag" to null in exporterOptions
    />
  ) : (
    <WrappedSpan>{rawState as any}</WrappedSpan>
  );
};

const WrappedSpan = styled("span")`
  white-space: pre-wrap;
`;
